import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

export default function StarfieldOverlay({
  density = 0.00012, // stars per pixel (auto scales)
  maxStars = 420,
  minStars = 140,
  speed = 0.18, // overall drift speed
  twinkle = 0.55, // twinkle strength
  className = "",
}) {
  const reduceMotion = useReducedMotion();
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    if (reduceMotion) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true });

    let w = 0;
    let h = 0;
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    let stars = [];
    let t0 = performance.now();

    const rand = (a, b) => a + Math.random() * (b - a);

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;

      const rect = parent.getBoundingClientRect();
      w = Math.max(1, Math.floor(rect.width));
      h = Math.max(1, Math.floor(rect.height));

      dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = Math.round(w * h * density);
      const count = Math.max(minStars, Math.min(maxStars, target));

      stars = new Array(count).fill(0).map(() => {
        const layer = Math.random(); // 0..1 (parallax depth)
        return {
          x: rand(0, w),
          y: rand(0, h),
          r: rand(0.6, 1.8) * (0.5 + layer), // sizes vary
          a: rand(0.25, 0.9),
          // random drift directions (not aligned)
          vx: rand(-1, 1) * speed * (0.15 + layer),
          vy: rand(-1, 1) * speed * (0.15 + layer),
          // twinkle: each star has its own frequency + phase
          tw: rand(0.6, 2.6) * (0.7 + layer),
          ph: rand(0, Math.PI * 2),
          layer,
        };
      });
    };

    const draw = (now) => {
      const dt = Math.min(48, now - t0);
      t0 = now;

      ctx.clearRect(0, 0, w, h);

      // Soft overall star haze
      ctx.globalCompositeOperation = "lighter";

      for (const s of stars) {
        // move
        s.x += s.vx * (dt / 16.67);
        s.y += s.vy * (dt / 16.67);

        // wrap edges
        if (s.x < -20) s.x = w + 20;
        if (s.x > w + 20) s.x = -20;
        if (s.y < -20) s.y = h + 20;
        if (s.y > h + 20) s.y = -20;

        // unsynced twinkle
        const twk = 1 + Math.sin(now / 1000 * s.tw + s.ph) * twinkle * (0.25 + s.layer);
        const alpha = Math.max(0, Math.min(1, s.a * twk));

        // draw glow (slight color variation)
        const hueShift = (s.layer * 18) - 6; // subtle cool variation
        const core = `rgba(255,255,255,${alpha})`;
        const glow = `hsla(${200 + hueShift}, 90%, 70%, ${alpha * 0.45})`;

        const r = s.r;
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, r * 6);
        g.addColorStop(0, core);
        g.addColorStop(0.25, glow);
        g.addColorStop(1, "rgba(0,0,0,0)");

        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x, s.y, r * 6, 0, Math.PI * 2);
        ctx.fill();

        // sharp core pixel
        ctx.fillStyle = `rgba(255,255,255,${alpha * 0.9})`;
        ctx.fillRect(s.x - 0.5, s.y - 0.5, 1, 1);
      }

      ctx.globalCompositeOperation = "source-over";
      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    rafRef.current = requestAnimationFrame(draw);

    const ro = new ResizeObserver(() => resize());
    if (canvas.parentElement) ro.observe(canvas.parentElement);

    const onDpr = () => resize();
    window.addEventListener("resize", onDpr);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      window.removeEventListener("resize", onDpr);
    };
  }, [reduceMotion, density, maxStars, minStars, speed, twinkle]);

  if (reduceMotion) return null;

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 ${className}`}
      style={{
        mixBlendMode: "screen",
        opacity: 0.85,
      }}
    />
  );
}
